import { useQueryClient } from '@tanstack/react-query';
import { can, colourHexFor } from '@pl/domain';
import type { ImportApplyDto, ImportMapping, ImportPreviewDto, ImportSummary, ImportWarning, SiteDto } from '@pl/domain';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { qk, useMe, useSiteMap } from '../../lib/queries';
import { Empty, ErrorNote, SiteTag, Spinner, Swatch } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import s from './SettingsImport.module.css';

/**
 * `POST /imports` takes a 15 MB body, and base64 grows a file by a third. Refusing a bigger file
 * here says why in words; letting it through gets the proxy's "Request body is too large".
 */
const MAX_FILE_BYTES = 11 * 1024 * 1024;
/** Beyond this many, the list says how many more rather than scrolling for ever. */
const WARNINGS_SHOWN = 100;
const TYPED_KEY_DELAY_MS = 500;

/** What one sheet column feeds. `qty:<siteId>` is a quantity column for that site. */
type ColumnRole = 'ignore' | 'skuCode' | 'name' | 'colourName' | 'attribute' | `qty:${string}`;

interface Draft {
  mapping: ImportMapping;
  deactivateMissing: boolean;
}

/** A → A, 26 → AA: the letter a spreadsheet shows above the column. */
function columnLetter(i: number): string {
  let n = i + 1;
  let out = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** "Jacket Type" → "jacket_type" — the same key the API suggests for an attribute column. */
function attrKey(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function roleOf(col: string, m: ImportMapping): ColumnRole {
  if (m.skuCode === col) return 'skuCode';
  if (m.name === col) return 'name';
  if (m.colourName === col) return 'colourName';
  if (Object.hasOwn(m.attributes, col)) return 'attribute';
  const site = m.quantities[col];
  if (site) return `qty:${site}`;
  return 'ignore';
}

const without = <T,>(rec: Record<string, T>, key: string): Record<string, T> => {
  const next = { ...rec };
  delete next[key];
  return next;
};

/**
 * Gives `col` its new role. SKU code, name and color are one column each, and each site takes one
 * quantity column, so claiming one of those releases whichever column held it — to Ignore, which
 * the table then shows. Nothing is reassigned behind the user's back.
 */
function assign(m: ImportMapping, col: string, role: ColumnRole, key: string): ImportMapping {
  const next: ImportMapping = {
    skuCode: m.skuCode === col ? '' : m.skuCode,
    name: m.name === col ? null : m.name,
    colourName: m.colourName === col ? null : m.colourName,
    attributes: without(m.attributes, col),
    quantities: without(m.quantities, col),
  };
  if (role === 'skuCode') next.skuCode = col;
  else if (role === 'name') next.name = col;
  else if (role === 'colourName') next.colourName = col;
  else if (role === 'attribute') next.attributes[col] = key;
  else if (role.startsWith('qty:')) {
    const siteId = role.slice(4);
    for (const [other, site] of Object.entries(next.quantities)) if (site === siteId) delete next.quantities[other];
    next.quantities[col] = siteId;
  }
  return next;
}

/** Why this mapping cannot be previewed or applied, in words, or null when it can. */
function problemOf(m: ImportMapping): string | null {
  if (!m.skuCode) return 'Choose the column that holds the SKU code. Rows are matched to your SKUs on it.';
  const seen = new Map<string, string>();
  for (const [col, key] of Object.entries(m.attributes)) {
    if (!key.trim()) return `Name the attribute that “${col}” fills.`;
    const earlier = seen.get(key.trim());
    if (earlier) return `“${earlier}” and “${col}” both fill the attribute “${key.trim()}”. Give one of them another name.`;
    seen.set(key.trim(), col);
  }
  return null;
}

/** Order-independent, so two drafts that say the same thing compare equal. */
function draftKey(d: Draft): string {
  const m = d.mapping;
  const sorted = (r: Record<string, string>) => Object.entries(r).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify([m.skuCode, m.name, m.colourName, sorted(m.attributes), sorted(m.quantities), d.deactivateMissing]);
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result ?? '');
      // "data:text/csv;base64,SXRlbS…" → "SXRlbS…"
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('This browser could not read the file.'));
    reader.readAsDataURL(file);
  });
}

function samplesFor(col: string, sample: Record<string, string>[]): string[] {
  const out: string[] = [];
  for (const row of sample) {
    const v = (row[col] ?? '').trim();
    if (v) out.push(v);
    if (out.length === 3) break;
  }
  return out;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

interface Loaded {
  importId: string;
  fileName: string;
  columns: string[];
  sample: Record<string, string>[];
  uploadedAt: Date;
}

export function SettingsImport() {
  const me = useMe().data;
  const siteMap = useSiteMap();
  const qc = useQueryClient();
  const id = useId();

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  // The numbers on screen: the last preview the server answered for the draft now showing.
  const [shown, setShown] = useState<{ summary: ImportSummary; warnings: ImportWarning[] } | null>(null);
  const [mapping, setMapping] = useState<ImportMapping | null>(null);
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [deactivate, setDeactivate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<unknown>(null);
  const [applied, setApplied] = useState<ImportApplyDto | null>(null);

  /*
   * `POST /imports/:id/apply` takes no mapping: it applies the one the server stored at the last
   * successful preview. So the mapping on screen and the mapping that gets applied are only the same
   * if every preview lands in the order it was sent and Apply waits for the last one. Previews are
   * therefore sent one at a time — a change made while one is out waits and goes next, and only the
   * newest waiting draft is sent — and Apply first lets that queue drain, then checks the server
   * confirmed exactly the draft on screen before it applies anything.
   */
  const draftRef = useRef<Draft | null>(null);
  const confirmedRef = useRef<string | null>(null);
  const queuedRef = useRef<Draft | null>(null);
  const pumpRef = useRef<Promise<void> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Bumped by a new upload or Start over, so an answer about a file that is gone is dropped.
  const generationRef = useRef(0);
  const importIdRef = useRef<string | null>(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const sites: SiteDto[] = useMemo(() => {
    const own = me?.sites ?? [];
    // A suggested quantity column for a site outside this user's own still needs a label.
    const extra = Object.values(mapping?.quantities ?? {})
      .filter((sid) => !own.some((x) => x.id === sid))
      .map((sid) => siteMap.get(sid))
      .filter((x): x is SiteDto => Boolean(x));
    return [...own, ...extra];
  }, [me, mapping, siteMap]);

  if (!me) return <Spinner label="Loading import" />;
  if (!can(me.role, 'import'))
    return (
      <div className="panel">
        <Empty title="Importing SKUs is for the office" description="Someone in the office or an admin can import your SKU sheet here." />
      </div>
    );

  const problem = mapping ? problemOf(mapping) : null;

  function sendPreview() {
    const d = draftRef.current;
    if (!d || problemOf(d.mapping)) return;
    queuedRef.current = d;
    if (!pumpRef.current) {
      pumpRef.current = pump().finally(() => {
        pumpRef.current = null;
        if (queuedRef.current) sendPreview();
      });
    }
  }

  async function pump() {
    const generation = generationRef.current;
    const importId = importIdRef.current;
    setPreviewing(true);
    try {
      while (queuedRef.current && generation === generationRef.current) {
        const d = queuedRef.current;
        queuedRef.current = null;
        try {
          const p = await api.post<ImportPreviewDto>(`/imports/${importId}/preview`, d);
          if (generation !== generationRef.current) return;
          confirmedRef.current = draftKey(d);
          if (!queuedRef.current) {
            setShown({ summary: p.summary, warnings: p.warnings });
            setPreviewError(null);
          }
        } catch (err) {
          if (generation !== generationRef.current) return;
          // The server kept whatever it stored before; which draft that was is no longer certain.
          confirmedRef.current = null;
          if (!queuedRef.current) setPreviewError(err);
        }
      }
    } finally {
      if (generation === generationRef.current) setPreviewing(false);
    }
  }

  function setDraft(next: Draft, when: 'now' | 'typing') {
    draftRef.current = next;
    setMapping(next.mapping);
    setDeactivate(next.deactivateMissing);
    setApplyError(null);
    clearTimeout(timerRef.current);
    if (problemOf(next.mapping)) return;
    if (when === 'now') sendPreview();
    else timerRef.current = setTimeout(sendPreview, TYPED_KEY_DELAY_MS);
  }

  function choose(col: string, role: ColumnRole) {
    if (!mapping) return;
    const key = keys[col] ?? attrKey(col);
    if (role === 'attribute' && keys[col] === undefined) setKeys((k) => ({ ...k, [col]: key }));
    setDraft({ mapping: assign(mapping, col, role, key.trim()), deactivateMissing: deactivate }, 'now');
  }

  function typeKey(col: string, value: string) {
    if (!mapping) return;
    setKeys((k) => ({ ...k, [col]: value }));
    setDraft({ mapping: { ...mapping, attributes: { ...mapping.attributes, [col]: value.trim() } }, deactivateMissing: deactivate }, 'typing');
  }

  function flushTyped() {
    const d = draftRef.current;
    if (!d || draftKey(d) === confirmedRef.current || problemOf(d.mapping)) return;
    clearTimeout(timerRef.current);
    sendPreview();
  }

  function reset() {
    generationRef.current++;
    clearTimeout(timerRef.current);
    draftRef.current = null;
    confirmedRef.current = null;
    queuedRef.current = null;
    importIdRef.current = null;
    setLoaded(null);
    setShown(null);
    setMapping(null);
    setKeys({});
    setDeactivate(false);
    setUploadError(null);
    setPreviewing(false);
    setPreviewError(null);
    setApplying(false);
    setApplyError(null);
    setApplied(null);
  }

  async function upload(file: File) {
    reset();
    const generation = generationRef.current;
    if (file.size === 0) {
      setUploadError(new Error(`${file.name} is empty.`));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setUploadError(new Error(`${file.name} is larger than 11 MB. Save just the SKU sheet as a CSV and try that.`));
      return;
    }
    setUploading(true);
    try {
      const contentBase64 = await readAsBase64(file);
      const p = await api.post<ImportPreviewDto>('/imports', { fileName: file.name, contentBase64 });
      if (generation !== generationRef.current) return;
      const draft: Draft = { mapping: p.mapping, deactivateMissing: false };
      importIdRef.current = p.importId;
      draftRef.current = draft;
      // The upload stored its suggested mapping and planned it without deactivation: that is this draft.
      confirmedRef.current = draftKey(draft);
      setLoaded({ importId: p.importId, fileName: p.fileName, columns: p.columns, sample: p.sample, uploadedAt: new Date() });
      setShown({ summary: p.summary, warnings: p.warnings });
      setMapping(p.mapping);
      setKeys({ ...p.mapping.attributes });
    } catch (err) {
      if (generation === generationRef.current) setUploadError(err);
    } finally {
      if (generation === generationRef.current) setUploading(false);
    }
  }

  async function apply() {
    const importId = importIdRef.current;
    if (!importId || applying || applied || !draftRef.current || problemOf(draftRef.current.mapping)) return;
    const generation = generationRef.current;
    setApplying(true);
    setApplyError(null);
    try {
      clearTimeout(timerRef.current);
      if (draftKey(draftRef.current) !== confirmedRef.current) sendPreview();
      while (pumpRef.current) await pumpRef.current;
      if (generation !== generationRef.current) return;
      const d = draftRef.current;
      if (!d || draftKey(d) !== confirmedRef.current) {
        setApplyError(new Error('The preview for these columns did not load, so nothing was imported. Try the preview again first.'));
        return;
      }
      const res = await api.post<ImportApplyDto>(`/imports/${importId}/apply`, { deactivateMissing: d.deactivateMissing });
      if (generation !== generationRef.current) return;
      setApplied(res);
      qc.invalidateQueries({ queryKey: qk.allSkus });
      qc.invalidateQueries({ queryKey: ['planned-quantities'] });
    } catch (err) {
      if (generation === generationRef.current) setApplyError(err);
    } finally {
      if (generation === generationRef.current) setApplying(false);
    }
  }

  const stage: 'upload' | 'map' | 'preview' | 'apply' | 'done' = applied ? 'done' : applying ? 'apply' : !loaded ? 'upload' : problem ? 'map' : 'preview';

  return (
    <div className={s.imPage}>
      <Stepper stage={stage} appliedNote={applied ? 'Applied' : null} />

      {!loaded && (
        <div className={`panel ${s.imUpload}`}>
          <span className={s.imIco}>
            <Icon name="sheet" />
          </span>
          <div className={s.imUploadText}>
            <b>Upload the sheet you keep your SKUs in</b>
            <p>
              A .csv or .xlsx file, with the column names in the first row and up to 5,000 rows. You choose what each column is next, and nothing changes until you
              apply.
            </p>
          </div>
          <div className={s.imPick}>
            <label htmlFor={`${id}-file`} className={s.imPickLabel}>
              Choose a CSV or Excel file
            </label>
            <input
              id={`${id}-file`}
              type="file"
              accept=".csv,.xlsx"
              className={s.imFile}
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void upload(file);
              }}
            />
          </div>
          {uploading && (
            <p className={s.imBusy} role="status">
              <Spinner label="Reading the file" /> Reading the file…
            </p>
          )}
          {uploadError ? (
            <div className={s.imWide}>
              <ErrorNote error={uploadError} />
            </div>
          ) : null}
        </div>
      )}

      {loaded && mapping && shown && (
        <>
          <div className="panel">
            <div className={s.imFileRow}>
              <span className={s.imIco}>
                <Icon name="sheet" />
              </span>
              <div className={s.imFileName}>
                <b>{loaded.fileName}</b>
                <small>
                  {plural(shown.summary.rows, 'row')} · {plural(loaded.columns.length, 'column')} · uploaded{' '}
                  {loaded.uploadedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} by {me.user.name}
                </small>
              </div>
              {!applied && (
                <span className={s.imReplace}>
                  <input
                    id={`${id}-replace`}
                    type="file"
                    accept=".csv,.xlsx"
                    className={s.imSrOnly}
                    disabled={applying}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void upload(file);
                    }}
                  />
                  <label htmlFor={`${id}-replace`} className="btn sm">
                    <Icon name="refresh" />
                    Replace file
                  </label>
                </span>
              )}
            </div>
          </div>

          {applied ? (
            <Result applied={applied} onAnother={reset} />
          ) : (
            <div className={s.imCols}>
              <MapTable
                columns={loaded.columns}
                sample={loaded.sample}
                mapping={mapping}
                keys={keys}
                sites={sites}
                disabled={applying}
                onChoose={choose}
                onKey={typeKey}
                onKeyDone={flushTyped}
                problem={problem}
              />

              <div className={`panel ${s.imPv}`}>
                <div className="panel-h">
                  <h2>Preview</h2>
                  <span className="grow" />
                  {previewing ? <Spinner label="Updating the preview" /> : <span className="sub">What applying would do</span>}
                </div>
                <Numbers summary={shown.summary} stale={previewing || Boolean(previewError) || Boolean(problem)} />
                {previewError ? (
                  <div className={s.imPvNote}>
                    <ErrorNote error={previewError} />
                    <button type="button" className="btn sm" onClick={sendPreview}>
                      Try again
                    </button>
                  </div>
                ) : null}
                {shown.warnings.length > 0 && <Warnings warnings={shown.warnings} />}
                <div className={s.imOpt}>
                  <Icon name="lock" />
                  <p>
                    Quantities are used only to plan stocktake stickers and to report starting drift. They never create stock. <strong>Only scanned boxes are stock.</strong>
                  </p>
                  <span className={s.imDerived}>
                    {Object.keys(mapping.quantities).length === 0 ? (
                      'No quantity columns chosen.'
                    ) : (
                      <>
                        {Object.values(mapping.quantities).map((sid) => (
                          <SiteTag key={sid} site={sites.find((x) => x.id === sid)} />
                        ))}
                        {plural(shown.summary.quantityCells, 'quantity cell')} to plan from
                      </>
                    )}
                  </span>
                </div>
                <div className={`${s.imOpt} ${s.imOptCheck} ${deactivate ? s.imOptOn : ''}`}>
                  <input
                    id={`${id}-deactivate`}
                    type="checkbox"
                    checked={deactivate}
                    disabled={applying}
                    aria-describedby={`${id}-deactivate-help`}
                    onChange={(e) => setDraft({ mapping, deactivateMissing: e.target.checked }, 'now')}
                  />
                  <label htmlFor={`${id}-deactivate`}>Deactivate SKUs that are not in this file</label>
                  <p id={`${id}-deactivate-help`}>They stay on every box and order that has them, but can’t be picked for new stickers or orders.</p>
                </div>
                {applyError ? (
                  <div className={s.imPvNote}>
                    <ErrorNote error={applyError} />
                  </div>
                ) : null}
                <div className={s.imPvF}>
                  <button type="button" className="btn ghost" onClick={reset} disabled={applying}>
                    Start over
                  </button>
                  <span className="grow" />
                  <button type="button" className="btn primary" onClick={() => void apply()} disabled={Boolean(problem) || applying}>
                    <Icon name="check" />
                    {applying ? 'Applying…' : 'Apply import'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'map', label: 'Map columns' },
  { key: 'preview', label: 'Preview' },
  { key: 'apply', label: 'Apply' },
] as const;

function Stepper({ stage, appliedNote }: { stage: 'upload' | 'map' | 'preview' | 'apply' | 'done'; appliedNote: string | null }) {
  const at = stage === 'done' ? STEPS.length : STEPS.findIndex((x) => x.key === stage);
  const items: ReactNode[] = [];
  STEPS.forEach((step, i) => {
    const state = i < at ? 'done' : i === at ? 'on' : '';
    if (i > 0) items.push(<div key={`${step.key}-line`} className={`step-line${i <= at ? ' done' : ''}`} aria-hidden="true" />);
    items.push(
      <div key={step.key} className={`step${state ? ` ${state}` : ''}`} aria-current={state === 'on' ? 'step' : undefined}>
        <span className="no">{state === 'done' ? <Icon name="check" /> : i + 1}</span>
        {step.label}
      </div>,
    );
  });
  return (
    <div className={`panel stepper ${s.imStepper}`} role="group" aria-label="Import steps">
      {items}
      <span className={s.imStepGap} />
      <span className="sub">{appliedNote ?? 'Nothing changes until you apply'}</span>
    </div>
  );
}

function MapTable({
  columns,
  sample,
  mapping,
  keys,
  sites,
  disabled,
  onChoose,
  onKey,
  onKeyDone,
  problem,
}: {
  columns: string[];
  sample: Record<string, string>[];
  mapping: ImportMapping;
  keys: Record<string, string>;
  sites: SiteDto[];
  disabled: boolean;
  onChoose: (col: string, role: ColumnRole) => void;
  onKey: (col: string, value: string) => void;
  onKeyDone: () => void;
  problem: string | null;
}) {
  const id = useId();
  const mapped = columns.filter((c) => roleOf(c, mapping) !== 'ignore').length;
  const ignored = columns.length - mapped;
  return (
    <div className={`panel ${s.imMp}`}>
      <div className="panel-h">
        <h2>Map columns</h2>
        <span className={`chip ${problem ? 'warn' : 'ok'} ${s.imChip}`}>
          <span className="dot" />
          {mapped} of {columns.length} mapped
        </span>
        <span className="grow" />
        <span className="sub">Rows match on SKU code</span>
      </div>
      <table className={`t ${s.imMap}`}>
        <thead>
          <tr>
            <th className={s.imThCol}>Sheet column</th>
            <th className={s.imArr} />
            <th className={s.imThField}>Platform field</th>
            <th>Sample values</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((col, i) => {
            const role = roleOf(col, mapping);
            const selectId = `${id}-c${i}`;
            const samples = samplesFor(col, sample);
            return (
              <tr key={col} className={role.startsWith('qty:') ? s.imQtyRow : undefined}>
                <td>
                  <span className={s.imCol}>
                    <span className={s.imL} aria-hidden="true">
                      {columnLetter(i)}
                    </span>
                    <label htmlFor={selectId} className={role === 'ignore' ? s.imColIgnored : undefined}>
                      {col}
                    </label>
                  </span>
                </td>
                <td className={s.imArr}>
                  <Icon name="arrow-right" />
                </td>
                <td>
                  <div className={s.imField}>
                    <div className={`inp ${s.imFsel} ${role === 'ignore' ? s.imFselIgnore : ''}`}>
                      <span className={s.imFace} aria-hidden="true">
                        <RoleFace role={role} sites={sites} />
                      </span>
                      <Icon name="chev-down" className="chev" />
                      <select id={selectId} className={s.imNative} value={role} disabled={disabled} onChange={(e) => onChoose(col, e.target.value as ColumnRole)}>
                        <option value="ignore">Ignore</option>
                        <option value="skuCode">SKU code</option>
                        <option value="name">Name</option>
                        <option value="colourName">Color</option>
                        <option value="attribute">Attribute</option>
                        {sites.map((site) => (
                          <option key={site.id} value={`qty:${site.id}`}>
                            Quantity at {site.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {role === 'attribute' && (
                      <input
                        className={`inp ${s.imKey} ${(keys[col] ?? '').trim() ? '' : 'err'}`}
                        aria-label={`Attribute key for ${col}`}
                        value={keys[col] ?? ''}
                        disabled={disabled}
                        spellCheck={false}
                        onChange={(e) => onKey(col, e.target.value)}
                        onBlur={onKeyDone}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onKeyDone();
                        }}
                      />
                    )}
                  </div>
                </td>
                <td>
                  <Samples role={role} values={samples} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={`panel-f ${s.imMpF}`}>
        {problem ? (
          <span className={s.imProblem} role="status">
            <Icon name="alert" />
            {problem}
          </span>
        ) : (
          <span className="sub">{ignored === 0 ? 'Every column is imported' : `${plural(ignored, 'column')} not imported`}</span>
        )}
      </div>
    </div>
  );
}

function RoleFace({ role, sites }: { role: ColumnRole; sites: SiteDto[] }) {
  if (role === 'skuCode')
    return (
      <>
        SKU code<span className={s.imKeyTag}>Key</span>
      </>
    );
  if (role === 'name') return <>Name</>;
  if (role === 'colourName') return <>Color</>;
  if (role === 'attribute') return <span className="pre">Attribute</span>;
  if (role.startsWith('qty:')) {
    const site = sites.find((x) => x.id === role.slice(4));
    return (
      <>
        <span className="pre">Quantity ·</span>
        {site ? <SiteTag site={site} /> : 'a site'}
      </>
    );
  }
  return <>Ignore</>;
}

function Samples({ role, values }: { role: ColumnRole; values: string[] }) {
  if (values.length === 0) return <span className="faint">—</span>;
  return (
    <div className={`${s.imSmp} ${role === 'ignore' ? 'faint' : ''}`}>
      {values.map((v, i) => (
        // The separator trails its value inside the same unbreakable item, so a line that wraps
        // ends on "·" rather than starting with one.
        <span key={i} className={s.imSmpItem}>
          {/* `title` carries the whole value for when the column is too narrow to show it. */}
          {role === 'colourName' ? (
            <span className={s.imC}>
              <Swatch hex={colourHexFor(v)} name={v} size="sm" />
              <span className={s.imSmpVal} title={v}>
                {v}
              </span>
            </span>
          ) : (
            <span className={`${s.imSmpVal} ${role === 'skuCode' ? 'mono' : role.startsWith('qty:') ? 'num' : ''}`} title={v}>
              {v}
            </span>
          )}
          {i < values.length - 1 && (
            <span className={s.imSep} aria-hidden="true">
              ·
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

function Numbers({ summary, stale }: { summary: ImportSummary; stale: boolean }) {
  const big: [string, number][] = [
    ['Rows in file', summary.rows],
    ['New', summary.new],
    ['Updated', summary.updated],
    ['Deactivated', summary.deactivated],
  ];
  const small: [string, number][] = [
    ['Unchanged', summary.unchanged],
    ['Quantity cells', summary.quantityCells],
    ['Skipped', summary.skipped],
  ];
  return (
    <div className={stale ? s.imStale : undefined} aria-busy={stale || undefined}>
      <div className={s.imNums}>
        {big.map(([k, v]) => (
          <div key={k}>
            <span className="metric-k">{k}</span>
            <span className={`metric-v num ${v === 0 ? 'faint' : ''}`}>{v.toLocaleString('en-US')}</span>
          </div>
        ))}
      </div>
      <div className={s.imNumsSm}>
        {small.map(([k, v]) => (
          <div key={k}>
            <span className="metric-k">{k}</span>
            <b className={`num ${v === 0 ? 'faint' : ''}`}>{v.toLocaleString('en-US')}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function Warnings({ warnings }: { warnings: ImportWarning[] }) {
  const shown = warnings.slice(0, WARNINGS_SHOWN);
  return (
    <div className={s.imWarn}>
      <Icon name="alert" />
      <div>
        <b>{plural(warnings.length, 'warning')}.</b> These rows import as described, or are skipped where the message says so.
        <ul className={s.imWarnList}>
          {shown.map((w, i) => (
            <li key={`${w.row}-${i}`}>{`Row ${w.row}: ${w.message}`}</li>
          ))}
        </ul>
        {warnings.length > shown.length && <p className={s.imWarnMore}>And {plural(warnings.length - shown.length, 'more warning')}.</p>}
      </div>
    </div>
  );
}

function Result({ applied, onAnother }: { applied: ImportApplyDto; onAnother: () => void }) {
  const { summary } = applied;
  return (
    <div className="panel">
      <div className="panel-h">
        <h2>Import applied</h2>
        <span className="chip ok">
          <span className="dot" />
          Done
        </span>
      </div>
      <div className={s.imDone}>
        <p className={s.imDoneLine} role="status">
          Imported: {summary.new.toLocaleString('en-US')} new, {summary.updated.toLocaleString('en-US')} updated
          {summary.unchanged ? `, ${summary.unchanged.toLocaleString('en-US')} unchanged` : ''}
          {summary.deactivated ? `, ${summary.deactivated.toLocaleString('en-US')} deactivated` : ''}.
        </p>
        <p className="sub">
          {summary.quantityCells
            ? `${plural(summary.quantityCells, 'quantity cell')} saved to plan stocktake stickers. Stock is unchanged: only scanned boxes are stock.`
            : 'Stock is unchanged: only scanned boxes are stock.'}
          {summary.skipped ? ` ${plural(summary.skipped, 'row')} skipped.` : ''}
        </p>
        {applied.warnings.length > 0 && <Warnings warnings={applied.warnings} />}
        <div className="row">
          <Link className="btn" to="/office/settings/skus">
            View SKUs
          </Link>
          <button type="button" className="btn ghost" onClick={onAnother}>
            Import another file
          </button>
        </div>
      </div>
    </div>
  );
}
